import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { BackFromCustomerDetailIcon } from "../../../assets";
import {
  SCREEN_PATH,
  EMPTY_STRING,
  PAYLOAD_KEYS,
  PAYMENT_DISABLED,
  adminReduxConstants,
  PROPERTY_LIMIT_EXCEEDED_MESSAGE,
} from "../../../constants";
import { IMAGE_TYPES } from "../../../system";
import {
  formatRating,
  buildFilterQuery,
  formatSubscriptionDetails,
} from "../../../shared/utility";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import ConfirmationModal from "../../../shared/components/modals/ConfirmationModal";
import PropertyFilter from "../../../shared/components/property-filter/PropertyFilter";
import PropertyCardAdmin from "../../../shared/components/property-card/PropertyCardAdmin";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions, systemActions } from "../../../redux/actions";

const { GET_AGENT_PROPERTIES, AGENT_PROPERTIES_PAGINATION } =
  adminReduxConstants;
const { FILTER_QUERY, SEARCH, ORDER_BY } = PAYLOAD_KEYS;

const AgentProperties = ({
  propertyId,
  allCountries,
  agentDetails,
  spinnerActions,
  getAgentDetails,
  agentProperties,
  getAgentProperties,
  updatePropertyStatus,
  isConfirmationModal,
  setIsConfirmationModal,
  agentPropertiesPagination,
  updatePagination,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    [spinnerActions]
  );

  const getFilterQuery = (data, countryFilter) => {
    const searchFields = ["Title"];

    // Build the initial filter query from the data
    let filter = buildFilterQuery(data, searchFields);

    // Dynamically append the filters based on their presence
    if (countryFilter && countryFilter !== EMPTY_STRING) {
      filter =
        filter !== EMPTY_STRING
          ? `(${filter}) && (${countryFilter})`
          : countryFilter;
    }

    return filter;
  };

  const getUpdatedPayload = () => {
    const updatedPayload = {
      ...agentPropertiesPagination,
      [FILTER_QUERY]: getFilterQuery(
        agentPropertiesPagination.search,
        agentPropertiesPagination.country
      ),
    };

    return updatedPayload;
  };

  const updateStatus = (pId) => {
    updatePropertyStatus(pId, () =>
      getAgentProperties(id, getUpdatedPayload())
    );
  };

  const onConfirmation = () => {
    setIsConfirmationModal(false);
    updatePropertyStatus(
      propertyId,
      () => getAgentProperties(id, getUpdatedPayload()),
      {
        isConfirm: true,
      }
    );
  };

  const handleUpdatePagination = (data, type) => {
    const payload = {
      ...agentPropertiesPagination,
      [SEARCH]: type === SEARCH ? data : agentPropertiesPagination.search,
      [ORDER_BY]: type === ORDER_BY ? data : agentPropertiesPagination.orderBy,
    };
    updatePagination(payload, AGENT_PROPERTIES_PAGINATION);
  };

  const handleChangeCountryFilter = (value) => {
    updatePagination(
      { ...agentPropertiesPagination, country: value },
      AGENT_PROPERTIES_PAGINATION
    );
  };

  const resetFilter = () => {
    updatePagination(
      {
        ...agentPropertiesPagination,
        search: EMPTY_STRING,
        orderBy: EMPTY_STRING,
        filterQuery: EMPTY_STRING,
        country: EMPTY_STRING,
      },
      AGENT_PROPERTIES_PAGINATION
    );
  };

  useEffect(() => {
    return () => {
      resetFilter();
    };
  }, []);

  useEffect(() => {
    getAgentDetails(id);
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    getAgentProperties(id, getUpdatedPayload());
  }, [
    id,
    agentPropertiesPagination.search,
    agentPropertiesPagination.orderBy,
    agentPropertiesPagination.country,
  ]);

  return (
    <div className="agent-info-wrapper">
      {spinner(GET_AGENT_PROPERTIES) > -1 && (
        <MainSpinner className="admin-spinner" />
      )}
      <ConfirmationModal
        onCancel={() => setIsConfirmationModal(false)}
        onConfirm={() => onConfirmation()}
        show={isConfirmationModal}
        message={PROPERTY_LIMIT_EXCEEDED_MESSAGE}
      />
      <MainContentWrapper className="position-relative">
        <img
          src={BackFromCustomerDetailIcon}
          className="customer-detail-back-icon pointer"
          onClick={() => navigate(`${SCREEN_PATH.ADMIN_AGENTS}/${id}`)}
          alt={EMPTY_STRING}
        />
        <div className="row">
          <div className="col-12 col-lg-8">
            <div className="admin-page-heading">Agents</div>
            <div className="agent-details-container">
              <div className="d-flex gap-3">
                <div className="agent-image">
                  <img
                    src={
                      agentDetails?.images?.filter(
                        (img) => img.imageType === IMAGE_TYPES.TYPE_HEADSHOT
                      )[0].image
                    }
                    alt={EMPTY_STRING}
                  />
                </div>
                <div className="d-flex flex-column justify-content-between align-items-start">
                  <div className="name mt-1">
                    {agentDetails.firstName} {agentDetails.lastName}
                  </div>
                  <div>
                    <div className="d-flex gap-1">
                      <div className="rating-label">Rating:</div>
                      <div className="rating-value">
                        {formatRating(agentDetails?.rating)}
                      </div>
                    </div>
                    <div className="d-flex gap-1">
                      <div className="rating-label">Properties Listed:</div>
                      <div className="rating-value">
                        {agentDetails?.propertiesCount}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Subscription Info Section */}
          <div className="col-12 col-lg-4 mt-2 mt-lg-0 text-start text-lg-end">
            <div className="subscription-heading">Subscription</div>
            <div className="subscription-description">
              {agentDetails?.isPaymentDisabled
                ? PAYMENT_DISABLED
                : formatSubscriptionDetails(agentDetails?.subscriptionPlan)}
            </div>
          </div>
        </div>

        <div className="agent-details-container">
          <div className="agent-info">
            <div className="agent-info-heading">
              Property Listings
              <div className="rounded-border mb-0" />
            </div>
          </div>
          <PropertyFilter
            allCountries={allCountries}
            searchValue={agentPropertiesPagination.search}
            countryValue={agentPropertiesPagination.country}
            orderByValue={agentPropertiesPagination.orderBy}
            onResetFilters={resetFilter}
            onFilterChange={handleUpdatePagination}
            onCountryChange={handleChangeCountryFilter}
            searchPlaceHolder="Search by property name"
          />
          <div className="row">
            {agentProperties.length > 0 ? (
              agentProperties.map((property) => (
                <div className="col-12 col-md-6" key={property.id}>
                  <PropertyCardAdmin
                    property={property}
                    updateStatus={updateStatus}
                  />
                </div>
              ))
            ) : (
              <div className="text-center mt-5 pt-5">No result found</div>
            )}
          </div>
        </div>
      </MainContentWrapper>
    </div>
  );
};

const mapStateToProps = (state) => ({
  allCountries: state.countryReducer.allCountries,
  propertyId: state.propertyReducer.propertyId,
  agentDetails: state.adminReducer.agentDetails,
  agentProperties: state.adminReducer.agentProperties,
  agentPropertiesPagination: state.adminReducer.agentPropertiesPagination,
  spinnerActions: state.systemReducer.spinnerActions,
  isConfirmationModal: state.systemReducer.isConfirmationModal,
});

const mapDispatchToProps = (dispatch) => ({
  getAgentDetails: (id) => dispatch(adminActions.getAgentDetails(id)),
  getAgentProperties: (id, body) =>
    dispatch(adminActions.getAgentProperties(id, body)),
  updatePropertyStatus: (id, func, body) =>
    dispatch(adminActions.updatePropertyStatus(id, func, body)),
  setIsConfirmationModal: (flag) =>
    dispatch(systemActions.setIsConfirmationModal(flag)),
  updatePagination: (data, type) =>
    dispatch(adminActions.updatePagination(data, type)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgentProperties);
